import { PropsWithChildren, useState } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { UserActionTypes } from '../../redux/user/types/UserActionTypes'
import { ROUTES } from '../../const/routes'
import { colors } from '../../const/colors'
import { checkRole } from '../../utils/checkRole'
import { checkFeature } from '../../utils/checkFeature'
import { PARKING_POOL_ROLES, HID_ROLES } from '../../const/permissions'
import { Features } from '../../const/features'
import { getEnv } from '../../utils/env'
import LogoCircle from '../Shared/LogoCircle'
import AppVersionLabel from '../Shared/AppVersionLabel'
import LanguageSwitch from './LanguageSwitch'
import { Roles } from '../../const/permissions'

interface ICustomWindow extends Window {
  TAG?: string
}
declare var window: ICustomWindow

const IcoBarChart = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
)

const IcoUsers = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <path d="M16 14c2.5 0 4.5 2 4.5 4.5" />
    <circle cx="17" cy="8.5" r="2.5" />
  </svg>
)

const IcoCalendar = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3.5" y="4.5" width="17" height="16" rx="3" />
    <path d="M3.5 9h17M8 3v3M16 3v3" />
  </svg>
)

const IcoBuildings = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3.5" y="8" width="7" height="12" rx="1" />
    <rect x="13.5" y="3.5" width="7" height="16.5" rx="1" />
    <path d="M6 11.5h2M6 15h2M16 7h2M16 11h2M16 15h2" />
  </svg>
)

const IcoCar = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11M5 11h14v5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H8v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-5Z" />
    <circle cx="8" cy="14" r="1" />
    <circle cx="16" cy="14" r="1" />
  </svg>
)

const IcoCard = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M3 10h18M7 14.5h4" />
  </svg>
)

const IcoTool = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.5 6a3.5 3.5 0 0 0-4.9 4.2l-5.4 5.4a1.7 1.7 0 0 0 2.4 2.4l5.4-5.4A3.5 3.5 0 0 0 18 8.1l-2 2-1.7-1.7 2-2" />
  </svg>
)

const IcoChevron = ({ open }: { open: boolean }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: 'transform .2s ease',
      transform: `rotate(${open ? '0' : '-90'}deg)`,
      flexShrink: 0,
    }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
)

interface ParkingSubItem {
  to: string
  labelKey: string
  icon: JSX.Element
  badgeKey?: string
  feature?: Features
}

const PARKING_SUBITEMS: ParkingSubItem[] = [
  {
    to: ROUTES.PARKING_POOL_DATA,
    labelKey: 'data',
    icon: <IcoBarChart />,
    feature: Features.mycompany_parking_stats,
  },
  { to: ROUTES.PARKING_POOL_USERS, labelKey: 'users_list', icon: <IcoUsers /> },
  {
    to: ROUTES.PARKING_POOL_RESERVATIONS,
    labelKey: 'company_reservations',
    icon: <IcoCalendar />,
  },
  {
    to: ROUTES.PARKING_POOL_OCCUPANCY_REPORT,
    labelKey: 'occupancy_report',
    icon: <IcoBuildings />,
  },
]

const CAT_BASE = {
  padding: '12px 15px',
  borderRadius: '12px',
  fontSize: '14.5px',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '11px',
} as const

const Navigation = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation(['admin_menu', 'btn', 'generic'])
  const dispatch = useAppDispatch()
  const { fullName, firstName, lastName, tenantName, roles, features } =
    useAppSelector(s => s.user)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const hasParkingAccess = checkRole(roles, PARKING_POOL_ROLES)
  const parkingSubItems = PARKING_SUBITEMS.filter(
    item => !item.feature || checkFeature(features, item.feature),
  )
  const hasHidAccess = checkRole(roles, HID_ROLES)
  const isActiveRoute = (route: string) =>
    pathname.startsWith(`/${ROUTES.ADMIN_BASE}/${route}`)
  const isParkingRoute = isActiveRoute(ROUTES.PARKING_POOL)

  const [parkingOpen, setParkingOpen] = useState(true)

  const logout = () => dispatch({ type: UserActionTypes.LOGOUT })

  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() ||
    fullName?.[0]?.toUpperCase() ||
    '?'

  const catStyle = (active: boolean) => ({
    ...CAT_BASE,
    color: active ? '#fff' : '#D5E2EA',
    ...(active
      ? {
          background: 'rgba(255,255,255,0.12)',
          boxShadow: 'inset 3px 0 0 #EDBD01',
        }
      : {}),
  })

  const subItemSx = (active: boolean) => ({
    padding: '11px 14px',
    borderRadius: '11px',
    fontSize: '13.5px',
    fontWeight: active ? 700 : 500,
    color: active ? '#fff' : '#A8BECC',
    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
    boxShadow: active ? 'inset 3px 0 0 #EDBD01' : 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'background .12s',
    '&:hover': { background: 'rgba(255,255,255,0.08)', color: '#fff' },
  })

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <Box
        component="header"
        sx={{
          height: '66px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 28px',
          background: '#fff',
          borderBottom: '1px solid #E7EDF1',
          position: 'relative',
          zIndex: 20,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LogoCircle />
          <Box
            sx={{
              fontSize: '18px',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
            }}
          >
            My{' '}
            <Box component="span" sx={{ color: colors.navy }}>
              Olivia
            </Box>
            {roles.includes(Roles.developer) && (
              <Box
                component="span"
                sx={{
                  fontSize: '12px',
                  color: colors.slate,
                  ml: '8px',
                  fontWeight: 400,
                }}
              >
                {window?.TAG || 'local'}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              fontSize: '11px',
              letterSpacing: '0.06em',
              color: colors.slate,
              fontWeight: 600,
              borderLeft: '1px solid #DEE6EB',
              paddingLeft: '12px',
              marginLeft: '4px',
              whiteSpace: 'nowrap',
            }}
          >
            {tenantName}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            fontSize: '14px',
            color: colors.textSecondary,
          }}
        >
          <LanguageSwitch />
          <Box
            component="a"
            href="https://myolivia.blob.core.windows.net/myolivia/docs/faq/html/mycompany_faq_pl_v1.html"
            target="_blank"
            sx={{
              fontWeight: 500,
              cursor: 'pointer',
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { color: colors.navy },
            }}
          >
            {t('admin_menu:faq')}
          </Box>
          <Box
            onClick={logout}
            sx={{
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': { color: colors.navy },
            }}
            data-e2e="button-logout"
          >
            {t('btn:logout')}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              paddingLeft: '8px',
              borderLeft: '1px solid #DEE6EB',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: '#E6EDF1',
                color: colors.navy,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '13px',
                flexShrink: 0,
              }}
            >
              {initials}
            </Box>
            <Box sx={{ lineHeight: 1.1 }}>
              <Box
                sx={{ fontWeight: 700, fontSize: '14px', color: colors.navy }}
              >
                {fullName}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          component="aside"
          sx={{
            width: '262px',
            flexShrink: 0,
            background: 'linear-gradient(180deg,#16425C 0%,#0E2C3B 100%)',
            padding: '18px 16px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              fontSize: '11px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
              fontWeight: 700,
              padding: '0 14px 12px',
            }}
          >
            {t('admin_menu:modules')}
          </Box>

          {hasParkingAccess && (
            <>
              <Box
                onClick={() => setParkingOpen(p => !p)}
                data-e2e="navigation-parking"
                sx={{
                  ...CAT_BASE,
                  color: isParkingRoute ? '#fff' : '#D5E2EA',
                  background: isParkingRoute
                    ? 'rgba(255,255,255,0.06)'
                    : 'transparent',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                  },
                }}
              >
                <IcoCar />
                <Box component="span" sx={{ flex: 1 }}>
                  {t('admin_menu:parking_areas')}
                </Box>
                <IcoChevron open={parkingOpen} />
              </Box>

              {parkingOpen && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    mt: '6px',
                    mb: '4px',
                    paddingLeft: '10px',
                    borderLeft: '2px solid rgba(255,255,255,0.14)',
                    marginLeft: '18px',
                  }}
                >
                  {parkingSubItems.map(item => (
                    <Box
                      key={item.to}
                      onClick={() => navigate(item.to)}
                      data-e2e={`navigation-parking-${item.labelKey}`}
                      sx={subItemSx(isActiveRoute(item.to))}
                    >
                      {item.icon}
                      <Box component="span" sx={{ flex: 1 }}>
                        {t(`admin_menu:${item.labelKey}`)}
                      </Box>
                      {item.badgeKey && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: '9.5px',
                            fontWeight: 800,
                            color: colors.navy,
                            background:
                              'linear-gradient(135deg,#F4D24A,#EDBD01)',
                            padding: '2px 7px',
                            borderRadius: '8px',
                            letterSpacing: '.03em',
                            flexShrink: 0,
                          }}
                        >
                          {t(`admin_menu:${item.badgeKey}`)}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}

          {hasHidAccess && (
            <Box
              onClick={() => navigate(ROUTES.HID_USERS)}
              data-e2e="navigation-hid"
              sx={{
                ...catStyle(isActiveRoute(ROUTES.HID_USERS)),
                '&:hover': {
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                },
              }}
            >
              <IcoCard />
              {t('admin_menu:mobile_access')}
            </Box>
          )}

          <Box
            component="a"
            href={`${getEnv('MYSOLVY_URL')}/my_tickets/?is_open=true`}
            target="_blank"
            sx={{
              ...catStyle(false),
              textDecoration: 'none',
              '&:hover': {
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
              },
            }}
          >
            <IcoTool />
            {t('admin_menu:my_solvy')}
          </Box>

          <AppVersionLabel
            onDark
            sx={{ marginTop: 'auto', padding: '18px 15px 4px' }}
          />
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            padding: '32px 36px 56px',
            background: colors.bg,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default Navigation
